import React from "react";
import { BaseComponent } from "@renta-apps/athenaeum-react-common";
import {
  Button,
  ButtonType,
  Dropdown,
  DropdownAlign,
  DropdownOrderBy,
  Form,
  IconSize,
  Inline,
  JustifyContent,
  SelectListItem,
  Spinner,
  TextInput,
  ToolbarButton,
  ToolbarContainer,
  ToolbarRow
} from "@renta-apps/athenaeum-react-components";
import ToolbarModel, { isAnySearchCriteriaSet, QueryTimeSpan } from "./ToolbarModel";
import EnumProvider from "@/providers/EnumProvider";
import Localizer from "../../../localization/Localizer";
import FeatureFlags from "@/helpers/FeatureFlags";
import AddConstructionSiteModal from "@/pages/ConstructionSites/AddConstructionSiteModal/AddConstructionSiteModal";
import OrganizationContract from "@/models/server/OrganizationContract";
import AddConstructionSiteRequest from "@/models/server/requests/AddConstructionSiteRequest";
import AddConstructionSiteResponse from "@/models/server/responses/AddConstructionSiteResponse";
import { FeatureSwitch } from "@/components/FeatureSwitch/FeatureSwitch";
import { ConstructionSiteStatus } from "@/models/Enums";

import styles from "./Toolbar.module.scss";
import UnleashHelper from "@/helpers/UnleashHelper";


interface IToolbarProps {
  model?: ToolbarModel;

  onChange?(model: ToolbarModel): Promise<void>;

  addConstructionSite(request: AddConstructionSiteRequest): Promise<AddConstructionSiteResponse>;
}

interface IToolbarState {
  model: ToolbarModel;
  extendedSearchVisible: boolean;
}

export default class Toolbar extends BaseComponent<IToolbarProps, IToolbarState> {
  addConstructionSiteModalId: string = "addConstructionSiteModal" as const;

  state: IToolbarState;

  constructor(props: IToolbarProps) {
    super(props);
    const model = this.props.model || new ToolbarModel();

    this.state = {
      model,
      extendedSearchVisible: model.status === ConstructionSiteStatus.Inactive && model.queryTimeSpan === QueryTimeSpan.Year,
    };
  }

  private static mergeState(prevState: IToolbarState, updatedProps: Partial<ToolbarModel>) {
    const updatedState = { ...prevState.model, ...updatedProps };
    if (updatedState.status === ConstructionSiteStatus.Inactive) {
      updatedState.queryTimeSpan = isAnySearchCriteriaSet(updatedState) ? QueryTimeSpan.Year : QueryTimeSpan.Initial;
    } else {
      updatedState.queryTimeSpan = QueryTimeSpan.AllTime;
    }
    return {
      model: updatedState,
      extendedSearchVisible: false,
    };
  }

  private async setFilter(update: Partial<ToolbarModel>): Promise<void> {
    this.setState(prevState => {
      return Toolbar.mergeState(prevState, update);
    });
  }

  private async setStatus(item: SelectListItem | null): Promise<void> {
    this.setState(prevState => {
      return Toolbar.mergeState(prevState, { status: (item) ? parseInt(item.value) : null });

    }, async () => {
      await this.props.onChange?.(this.state.model);
    });
  }

  private async clearFilter() {
    this.setState({
      model: new ToolbarModel(),
      extendedSearchVisible: false,
    },
      async () => {
        this.props.onChange?.(this.state.model);
      });
  }

  private async submitSearchForm() {
    this.setState(prevState => {
      const updatedModel = { ...prevState.model, filtersSubmitted: true };
      if (updatedModel.status !== ConstructionSiteStatus.Inactive) {
        return { model: updatedModel, extendedSearchVisible: false };
      } else {
        return { model: updatedModel, extendedSearchVisible: updatedModel.queryTimeSpan === QueryTimeSpan.Year };
      }
    },
      async () => {
        await this.props.onChange?.(this.state.model);
      }
    )
  }

  private async extendSearch() {
    this.setState(prevState => ({
      model: { ...prevState.model, queryTimeSpan: QueryTimeSpan.AllTime },
      extendedSearchVisible: false,
    }), async () => {
      await this.props.onChange?.(this.state.model);
    });
  }

  private async fetchCustomersAsync(): Promise<OrganizationContract[]> {
    return await this.postAsync("api/constructionSiteManagement/getAllCustomers");
  }


  private renderSearchInput() {
    if (UnleashHelper.isEnabled(FeatureFlags.ConstructionSiteDetailedSearch)) {
      return (
        <>
          {!this.state.model.filtersSubmitted && (
            <Inline>
              <span>{Localizer.constructionSitesSearchInfo}</span>
            </Inline>
          )}

          <Inline>

            <TextInput id="customerNumber" inline small
              width="10rem"
              placeholder={Localizer.genericCustomerNumber}
              value={this.state.model.customerNumber}
              onChange={async (_, value) => {
                await this.setFilter({ customerNumber: value });
              }}
            />

            <TextInput id="customerName" inline small
              width="10rem"
              placeholder={Localizer.genericCustomerName}
              value={this.state.model.customerName}
              onChange={async (_, value) => {
                await this.setFilter({ customerName: value });
              }}
            />

            <TextInput id="siteNumber" inline small
              width="12rem"
              placeholder={Localizer.constructionSitesToolbarSiteNumber}
              value={this.state.model.siteNumber}
              onChange={async (_, value) => {
                await this.setFilter({ siteNumber: value });
              }}
            />

            <TextInput id="siteName" inline small
              width="12rem"
              placeholder={Localizer.constructionSitesToolbarSiteName}
              value={this.state.model.siteName}
              onChange={async (_, value) => {
                await this.setFilter({ siteName: value });
              }}
            />

            <TextInput id="invoiceReference" inline small
              width="10rem"
              placeholder={Localizer.constructionSitesToolbarInvoiceReference}
              value={this.state.model.invoiceReference}
              onChange={async (_, value) => await this.setFilter({ invoiceReference: value })}
            />

            <TextInput id="externalReference" inline small
              width="10rem"
              placeholder={Localizer.constructionSitesToolbarExternalReference}
              value={this.state.model.externalReference}
              onChange={async (_, value) => {
                await this.setFilter({ externalReference: value });
              }}
            />

            <TextInput id="address" inline small
              width="10rem"
              placeholder={Localizer.genericAddress}
              value={this.state.model.address}
              onChange={async (_, value) => {
                await this.setFilter({ address: value });
              }}
            />

            <Button submit small
              id="filterConstructionSites"
              label={Localizer.addConstructionsiteToolbarFilter}
              icon={{ name: "fas search" }}
              type={ButtonType.Orange}
            />

          </Inline>
        </>
      )
    } else {
      return (
        <Inline>

          <TextInput id="searchTerm" inline small
            placeholder={Localizer.constructionSitesToolBarSearchTermFilterWithProjectNumber}
            value={this.state.model.searchTerm}
            width="22rem"
            onChange={async (_, value) => {
              await this.setFilter({ searchTerm: value });
            }}
          />

          <Button small submit
            id="filterConstructionSites"
            label={Localizer.addConstructionsiteToolbarFilter}
            icon={{ name: "fas search" }}
            type={ButtonType.Orange}
          />

        </Inline>

      )
    }
  }

  public render(): React.ReactNode {

    return (
      <ToolbarContainer className={styles.toolbar}>

        <ToolbarRow justify={JustifyContent.SpaceBetween}>

          <Form onSubmit={async () => await this.submitSearchForm()}>

            {this.renderSearchInput()}

            <Inline>
              <Dropdown inline small noValidate noSubtext noWrap autoCollapse align={DropdownAlign.Left}
                id="constructionSitesStatus"
                nothingSelectedText={Localizer.addConstructionsiteToolbarAnyStatus}
                minWidth="7rem"
                orderBy={DropdownOrderBy.None}
                items={EnumProvider.getConstructionSiteStatusItems()}
                selectedItem={(this.state.model.status != null) ? EnumProvider.getConstructionSiteStatusItem(this.state.model.status) : undefined}
                onChange={async (_, item) => await this.setStatus(item)}
              />


              <Button small title={Localizer.addConstructionsiteToolbarClearFilter}
                id="clearConstructionSitesFilters"
                icon={{ name: "far history", size: IconSize.Large }}
                type={ButtonType.Info}
                onClick={async () => {
                  await this.clearFilter();
                }}
              />

              {this.state.extendedSearchVisible && (
                <>
                  <span style={{ cursor: 'pointer', marginLeft: '10px', display: 'inline-block' }}>
                    {Localizer.constructionSitesExtendedSearch}
                  </span>
                  <Button small submit
                    id="filterConstructionSitesExtendSearch"
                    className={styles.extendSearchButton}
                    label={Localizer.genericYes}
                    icon={{ name: "fas search" }}
                    type={ButtonType.Default}
                    onClick={() => this.extendSearch()}
                  />
                </>


              )}

            </Inline>
          </Form>

          <FeatureSwitch flagName={FeatureFlags.ConstructionSiteIntegration}>
            <ToolbarButton id="addConstructionSiteButton"
              label={Localizer.addConstructionsiteAddSite}
              icon={{ name: "plus", size: IconSize.Large }}
              type={ButtonType.Orange}
              dataTarget={this.addConstructionSiteModalId}
              toggleModal
            />
          </FeatureSwitch>
        </ToolbarRow>

        {
          (this.isSpinning()) && <Spinner />
        }
        <FeatureSwitch flagName={FeatureFlags.ConstructionSiteIntegration}>
          <AddConstructionSiteModal modalId={this.addConstructionSiteModalId}
            fetchCustomers={async () => this.fetchCustomersAsync()}
            addConstructionSite={this.props.addConstructionSite}
          />
        </FeatureSwitch>
      </ToolbarContainer>
    );
  }
};